import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AuthState } from "../../context/auth/auth/reducer";
import {
  getUserDetail,
  updateUserDetail,
  updateUserProfilePicture,
} from "../../context/users/action";
import PersonalDataForm from "../../components/profile/forms/PersonalDataForm";
import CfuDataForm from "../../components/profile/forms/CfuDataForm";
import { IToastContext, ToastContext } from "../../providers/Toast";
import CompanyDataForm from "../../components/profile/forms/CompanyDataForm";
import MyEventsList from "./events/list";
import ImageFileUpload from "../../components/shared/forms/fileUpload/ImageFileUpload";
import MyAppointmentsList from "./appointments/list";
import ProfileMenu from "./menu/view";
import ProfileUserInfo from "./components/info/ProfileUserInfo";
import ProfileBusinessCards from "./businessCards/view";
import MyContactsList from "./contacts/list";
import { deleteUserProfile, signOut } from "../../context/auth/action";
import { IModalContext, ModalContext } from "../../providers/Modal";
import { UserDetail } from "../../data/interfaces";
import ExportPersonalData from "./components/export/ExportPersonalData";
import moment from "moment";
import ProfileChatView from "./chat/view";
import _environment from "../../_environment/_environment";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";
import queryString from "query-string";
import { useHistory } from "react-router-dom";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {}

interface PendingState {
  personalData?: boolean;
  companyData?: boolean;
  cfuData?: boolean;
  profileImage?: boolean;
}

const ProfileView = ({ ...props }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------

  const history = useHistory();

  // Reference to Redux store dispatch function: use it to dispatch actions.
  const dispatch = useDispatch();

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to modal manager hook that you may use to open and close modals.
  const { openModal } = useContext<IModalContext>(ModalContext);

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------
  const { user: authUser } = useSelector(({ authReducer }: { authReducer: AuthState }) => {
    return authReducer;
  });

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  // To handle profile sections (left menu)
  const [section, setSection] = useState<string>("");

  // To handle forms submit
  const [isPending, setIsPending] = useState<PendingState>({
    personalData: false,
    companyData: false,
    cfuData: false,
    profileImage: false,
  });

  // User data
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserDetail | null>(null);

  const [events, setEvents] = useState<any[] | null>(null);
  const [contacts, setContacts] = useState<any[] | null>(null);
  const [appointments, setAppointments] = useState<any[] | null>(null);
  const [businessCards, setBusinessCards] = useState<any[] | null>(null);

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  const loadUserData = async () => {
    setUserLoading(true);
    try {
      const data = await getUserDetail(authUser);
      setUserData(data);
    } catch (error) {
    } finally {
      setUserLoading(false);
    }
  };

  /**
   * Update user's profile picture
   * @function
   * @async
   *
   * @param url
   */
  const onUpdateProfileImage = async (url: any) => {
    setIsPending({
      profileImage: true,
    });
    try {
      await updateUserProfilePicture(url, authUser?.uid);
      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.profile_pic_upload",
        callback: () => {},
      });

      dispatch({
        type: "USER_UPDATE",
        payload: {
          ...authUser,
          ...{ profile_image_url: url },
        },
      });

      // Track event
      trackAnalyticsEvent(ANALYTICS_EVENTS.UPDATE_PROFILE_IMG, { email: authUser?.email });
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setIsPending({
        profileImage: false,
      });
    }
  };

  /**
   * Update user's personal data
   * @function
   * @async
   *
   * @param data
   */
  const onPersonalDataSubmit = async (data: any) => {
    setIsPending({
      personalData: true,
    });
    try {
      await updateUserDetail(authUser, data);

      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.data_updated",
        callback: () => {},
      });

      // Update on store for useful vars
      dispatch({
        type: "USER_UPDATE",
        payload: {
          ...authUser,
          ...{
            phone_number: data.phone_number,
            international_buyer: data.international_buyer,
          },
        },
      });

      // Track event
      trackAnalyticsEvent(ANALYTICS_EVENTS.UPDATE_PERSONAL_DATA, { email: authUser?.email });

      // Update state
      setUserData({
        ...userData,
        ...data,
      });
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setIsPending({
        personalData: false,
      });
    }
  };

  /**
   * Update user's company data
   * @function
   * @async
   *
   * @param data
   */
  const onCompanyDataSubmit = async (data: any) => {
    setIsPending({
      companyData: true,
    });
    try {
      await updateUserDetail(authUser, data);
      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.data_updated",
        callback: () => {},
      });

      // Update on store for company name and user role
      dispatch({
        type: "USER_UPDATE",
        payload: {
          ...authUser,
          ...{ company: data.company.name, company_role: data.company.user_role },
        },
      });

      // Track event
      trackAnalyticsEvent(ANALYTICS_EVENTS.UPDATE_BUSINESS_DATA, { email: authUser?.email });

      // Update state
      setUserData({
        ...userData,
        ...data,
      });
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setIsPending({
        companyData: false,
      });
    }
  };

  /**
   * Update user's cfu data
   * @function
   * @async
   *
   * @param data
   */
  const onCfuDataSubmit = async (data: any) => {
    setIsPending({
      cfuData: true,
    });
    try {
      await updateUserDetail(authUser, data);
      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.data_updated",
        callback: () => {},
      });

      // Update state
      setUserData({
        ...userData,
        ...data,
      });
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setIsPending({
        cfuData: false,
      });
    }
  };

  /**
   * Delete user's profile (uses a callable http cloud function)
   * @function
   * @async
   */
  const deleteProfile = async () => {
    try {
      await deleteUserProfile(authUser?.uid);
      dispatch(signOut());
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    }
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Renders user's default profile view
   * @function
   */
  const renderDefault = (): React.ReactNode => {
    return (
      <div className="profile-section">
        <div className="header">
          <h1>{t("pages.profile.default.title")}</h1>
          <p className="desc mb-4 ">{t("pages.profile.default.description")}</p>
          <div className="image">
            <img src="/assets/images/undraw/profile.svg" />
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders a form with file input to upload new profile image
   * @function
   */
  const renderProfileImageEdit = (): React.ReactNode => {
    return (
      <>
        <h1 className="h4 text-gray-900 mb-2">
          {t("pages.profile.forms.personal_data.profile_picture")}
        </h1>
        <ImageFileUpload
          fileName={`${userData?.firstname}_${userData?.lastname}_${moment().unix()}`}
          bucket={_environment.firebase.storage.buckets.resize}
          folder={_environment.firebase.storage.paths.profile_images}
          onUploadComplete={onUpdateProfileImage}
        />
      </>
    );
  };

  /**
   * Renders user's personal data
   * @function
   */
  const renderPersonalData = (): React.ReactNode => {
    return (
      <div className="profile-section">
        <div className="header">
          <h1>{t("pages.profile.my_personal_information.title")}</h1>
          <p className="desc mb-4 ">{t("pages.profile.my_personal_information.description")}</p>
          <div className="image">
            <img src="/assets/images/undraw/personal.png" />
          </div>
        </div>

        <PersonalDataForm
          user={userData}
          showDesc={false}
          onSubmit={onPersonalDataSubmit}
          pending={isPending.personalData}
        />
        <hr />
        {renderProfileImageEdit()}

        <hr />
        {/* Export account data */}
        <ExportPersonalData userData={userData} />

        <hr />
        {/* Delete profile */}
        <small
          className={"link"}
          onClick={() =>
            openModal({
              title: t("pages.profile.remove_my_profile.title"),
              message: t("pages.profile.remove_my_profile.message"),
              callback: () => deleteProfile,
            })
          }
        >
          {t("pages.profile.remove_my_profile.title")}
        </small>
      </div>
    );
  };

  /**
   * Renders user's business data
   * @function
   */
  const renderBusinessData = (): React.ReactNode => {
    return (
      <div className="profile-section">
        <div className="header">
          <h1>{t("pages.profile.my_business_information.title")}</h1>
          <p className="desc mb-4 ">{t("pages.profile.my_business_information.description")}</p>
          <div className="image">
            <img src="/assets/images/undraw/business.png" />
          </div>
        </div>

        <CompanyDataForm
          user={userData}
          showDesc={false}
          onSubmit={onCompanyDataSubmit}
          pending={isPending.companyData}
        />
      </div>
    );
  };

  /**
   * Renders user's cfu data
   * @function
   */
  const renderCfuData = (): React.ReactNode => {
    return (
      <div className="profile-section">
        <div className="header">
          <h1>{t("pages.profile.forms.cfu_data.title")}</h1>
          <p className="desc mb-4 ">{t("pages.profile.forms.cfu_data.description")}</p>
          <div className="image">
            <img src="/assets/images/undraw/credits.png" />
          </div>
        </div>
        <CfuDataForm
          showTitle={false}
          showDesc={false}
          user={userData}
          onSubmit={onCfuDataSubmit}
          pending={isPending.cfuData}
        />
      </div>
    );
  };

  /**
   * Renders user's events (events where the user participated)
   * @function
   */
  const renderEvents = (): React.ReactNode => {
    return <MyEventsList loadedData={events} onLoad={setEvents} uid={authUser?.uid} />;
  };

  /**
   * Renders user's contacts (members or other users)
   * @function
   */
  const renderContacts = (): React.ReactNode => {
    return <MyContactsList loadedData={contacts} onLoad={setContacts} uid={authUser?.uid} />;
  };

  /**
   * Renders user appointments
   * @function
   */
  const renderAppointments = (): React.ReactNode => {
    return userData ? (
      <MyAppointmentsList
        loadedData={appointments}
        onLoad={setAppointments}
        userData={userData}
        uid={authUser?.uid}
      />
    ) : null;
  };

  /**
   * Renders user businessCards (business cards left to stands)
   * @function
   */
  const renderBusinessCards = (): React.ReactNode => {
    return (
      <ProfileBusinessCards
        loadedData={businessCards}
        onLoad={setBusinessCards}
        uid={authUser?.uid}
      />
    );
  };

  /**
   * Renders user's chat data
   * @function
   */
  const renderChat = (): React.ReactNode => {
    return <ProfileChatView />;
  };

  /**
   * Renders active section (based on menu selection)
   * @function
   */
  const renderSection = (): React.ReactNode => {
    return (
      <>
        {(() => {
          switch (section) {
            case "personal":
              return renderPersonalData();
            case "business": {
              return renderBusinessData();
            }
            case "contacts": {
              return renderContacts();
            }
            case "cfu": {
              return renderCfuData();
            }
            case "events":
              return renderEvents();
            case "appointments":
              return renderAppointments();
            case "businessCards":
              return renderBusinessCards();
            case "chat":
              return renderChat();
            default:
              return renderDefault();
          }
        })()}
      </>
    );
  };

  const goToSection = (section: string): void => {
    setSection(section);
    // Save section on query params
    history.push({
      pathname: history.location.pathname,
      search: `?section=${section}`,
    });
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[ProfileView] init");

    if (!!authUser && !!authUser.profile_completed) {
      loadUserData();
    }

    // Read query params to set section
    const params = queryString.parse(history.location.search);

    if (!!params.section) {
      setSection(params.section.toString());
    }
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[ProfileView] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="profile-page">
      <div className="container">
        {!!userLoading && (
          <div className="list-loading">
            <i className="fas fa-spin fa-spinner"></i>
          </div>
        )}

        {!userLoading && (
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4 col-xlg-3">
              <div className="box o-hidden">
                <div className="user-info">
                  <ProfileUserInfo userData={userData} />
                </div>
                <div className="profile-sections">
                  <ProfileMenu activeSection={section} onChange={goToSection} />
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-8 col-lg-8 col-xlg-9">
              <div className="box p-relative content-box">{renderSection()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ProfileView;
